import { Button } from '@components/common';
import { FirstTimeSetupStepCard } from '@components/onboarding/FirstTimeSetupStepCard';
import { useAppDispatch, useAppSelector } from '@hooks/useRedux';
import {
  selectCurrentUser,
  selectNeedsFirstTimeOnboarding,
} from '@store/auth/authSelectors';
import { fetchCurrentUser } from '@store/auth/authThunks';
import {
  Building2,
  Grid2X2,
  ListChecks,
  MenuSquare,
  Printer,
  Receipt,
  Rocket,
  ShieldCheck,
  ShoppingCart,
  Sparkles,
  Store,
  Users,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  PRIMARY_FIRST_TIME_ONBOARDING_STEP_IDS,
  completeFirstTimeOnboarding,
  ensureFirstTimeOnboardingProgress,
  setFirstTimeOnboardingCurrentStep,
  updateFirstTimeOnboardingStep,
} from '../services/firstTimeOnboardingService';
import { queueOnboardingTour } from '../services/onboardingTourService';
import { UserOnboardingProgress, UserOnboardingStepId } from '../types';
import { apiError, apiSuccess } from '../util/notify';

type SetupStep = {
  actionLabel: string;
  description: string;
  icon: typeof Store;
  id: UserOnboardingStepId;
  isOptional?: boolean;
  route: string;
  title: string;
};

const SETUP_STEPS: SetupStep[] = [
  {
    actionLabel: 'Open outlets',
    description:
      'Your first outlet was created during signup. Review it, update its details, or add more outlets before you continue.',
    icon: Store,
    id: 'outlets',
    route: '/dashboard/outlets',
    title: 'Review your outlets',
  },
  {
    actionLabel: 'Open departments',
    description:
      'Create departments first, because menu items are tied to departments for routing and printer configuration.',
    icon: Building2,
    id: 'departments',
    route: '/dashboard/departments',
    title: 'Create departments',
  },
  {
    actionLabel: 'Open menu',
    description:
      'Build your menu by creating items, categories, and add-ons so the sales screen has products ready to sell.',
    icon: MenuSquare,
    id: 'menu',
    route: '/dashboard/menu',
    title: 'Create your menu',
  },
  {
    actionLabel: 'Open sections & tables',
    description:
      'Start from the Sections tab, then create the tables for each section so dining orders can be opened correctly.',
    icon: Grid2X2,
    id: 'sections_tables',
    route: '/dashboard/sections-tables',
    title: 'Set up sections and tables',
  },
  {
    actionLabel: 'Open printer setup',
    description:
      'Add outlet printers under Settings → Printer Settings, then use Printer Configuration to map bill printers and department KOT printers for each section.',
    icon: Printer,
    id: 'section_printers',
    route: '/settings/printers/configuration',
    title: 'Configure section printers',
  },
  {
    actionLabel: 'Open takeaway printers',
    description:
      'Set up takeaway printer routing so takeaway orders print the right KOTs and bills for each department.',
    icon: Receipt,
    id: 'takeaway_printers',
    route: '/settings/printers/configuration',
    title: 'Configure takeaway printers',
  },
  {
    actionLabel: 'Open sales',
    description:
      'Create your first order by selecting a table, confirming the order, adding items, then using waiter, shift, KOT, bill, settle, and discount actions as needed.',
    icon: ShoppingCart,
    id: 'first_order',
    route: '/sales',
    title: 'Create your first order',
  },
  {
    actionLabel: 'Open users',
    description:
      'Invite employee accounts and assign them to the right outlet so the team can sign in and start working.',
    icon: Users,
    id: 'users',
    isOptional: true,
    route: '/settings/users',
    title: 'Create user accounts',
  },
  {
    actionLabel: 'Open security',
    description:
      'Define access controls and attach them to role groups so each team member only sees what they should.',
    icon: ShieldCheck,
    id: 'security',
    isOptional: true,
    route: '/settings/security/access-controls',
    title: 'Set up access controls',
  },
];

const FirstTimeSetupPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const currentUser = useAppSelector(selectCurrentUser);
  const needsFirstTimeOnboarding = useAppSelector(
    selectNeedsFirstTimeOnboarding,
  );
  const currentOnboardingStep = currentUser?.onboarding?.current_step;
  const currentOutletId = currentUser?.outlet_id;

  const [progress, setProgress] = useState<UserOnboardingProgress | null>(null);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [savingStepId, setSavingStepId] = useState<UserOnboardingStepId | null>(
    null,
  );
  const [isFinishing, setIsFinishing] = useState(false);

  useEffect(() => {
    let disposed = false;

    if (!currentUser?.id) {
      return () => {
        disposed = true;
      };
    }

    const initializeProgress = async () => {
      setIsBootstrapping(true);
      const response = await ensureFirstTimeOnboardingProgress(
        currentOnboardingStep || PRIMARY_FIRST_TIME_ONBOARDING_STEP_IDS[0],
      );

      if (!response.success || !response.data) {
        if (!disposed) {
          apiError(
            response.error || 'Failed to load your getting-started checklist.',
          );
          setIsBootstrapping(false);
        }
        return;
      }

      let nextProgress = response.data;

      if (
        currentOutletId &&
        !nextProgress.completed_steps.includes('outlets')
      ) {
        const autoStepResponse = await updateFirstTimeOnboardingStep(
          nextProgress,
          'outlets',
          true,
        );

        if (autoStepResponse.success && autoStepResponse.data) {
          nextProgress = autoStepResponse.data;
        }
      }

      if (!disposed) {
        setProgress(nextProgress);
        setIsBootstrapping(false);
      }

      await dispatch(fetchCurrentUser());
    };

    initializeProgress().catch((error) => {
      if (!disposed) {
        apiError(
          error instanceof Error
            ? error.message
            : 'Failed to load your getting-started checklist.',
        );
        setIsBootstrapping(false);
      }
    });

    return () => {
      disposed = true;
    };
  }, [currentOnboardingStep, currentOutletId, currentUser?.id, dispatch]);

  const primarySteps = useMemo(
    () => SETUP_STEPS.filter((step) => !step.isOptional),
    [],
  );
  const secondarySteps = useMemo(
    () => SETUP_STEPS.filter((step) => step.isOptional),
    [],
  );

  const completedPrimaryCount = useMemo(
    () =>
      primarySteps.filter((step) => progress?.completed_steps.includes(step.id))
        .length,
    [primarySteps, progress?.completed_steps],
  );

  const arePrimaryStepsComplete =
    completedPrimaryCount === PRIMARY_FIRST_TIME_ONBOARDING_STEP_IDS.length;

  const nextRecommendedStep = useMemo(
    () =>
      primarySteps.find(
        (step) => !progress?.completed_steps.includes(step.id),
      ) ||
      secondarySteps.find(
        (step) => !progress?.completed_steps.includes(step.id),
      ),
    [primarySteps, progress?.completed_steps, secondarySteps],
  );

  const handleToggleStep = async (stepId: UserOnboardingStepId) => {
    if (!progress) {
      return;
    }

    const isCompleted = progress.completed_steps.includes(stepId);
    setSavingStepId(stepId);

    const response = await updateFirstTimeOnboardingStep(
      progress,
      stepId,
      !isCompleted,
    );

    if (!response.success || !response.data) {
      apiError(response.error || 'Failed to update your onboarding progress.');
      setSavingStepId(null);
      return;
    }

    setProgress(response.data);
    await dispatch(fetchCurrentUser());
    setSavingStepId(null);

    if (!isCompleted && response.data.status === 'completed') {
      apiSuccess(
        'Your core setup is complete. You are ready to start selling.',
      );
    }
  };

  const handleStartGuide = async (step: SetupStep) => {
    if (progress) {
      const response = await setFirstTimeOnboardingCurrentStep(
        progress,
        step.id,
      );

      if (response.success && response.data) {
        setProgress(response.data);
        await dispatch(fetchCurrentUser());
      }
    }

    queueOnboardingTour(step.id);
    navigate(step.route);
  };

  const handleFinish = async () => {
    if (!progress || !arePrimaryStepsComplete) {
      return;
    }

    setIsFinishing(true);
    const response = await completeFirstTimeOnboarding(progress);

    if (!response.success || !response.data) {
      apiError(response.error || 'Failed to complete onboarding.');
      setIsFinishing(false);
      return;
    }

    setProgress(response.data);
    await dispatch(fetchCurrentUser());
    setIsFinishing(false);
    navigate('/sales');
  };

  if (!currentUser) {
    return <Navigate to="/" replace />;
  }

  if (
    !needsFirstTimeOnboarding &&
    currentUser.company?.created_by !== currentUser.id &&
    !currentUser.onboarding
  ) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="space-y-6">
      <div className="overflow-hidden rounded-[28px] border border-emerald-100 bg-gradient-to-br from-white via-emerald-50/70 to-emerald-100/70 shadow-sm">
        <div className="grid gap-6 px-7 py-7 lg:grid-cols-[1.4fr_0.8fr] lg:items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-white/80 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-emerald-700">
              <Sparkles size={14} />
              First-time setup
            </div>
            <h2 className="mt-5 text-3xl font-semibold text-gray-950">
              Set up smartPOS Cloud the right way
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-gray-600">
              Your billing and company are ready. Next, we&apos;ll walk you
              through the setup order that gets restaurants live faster:
              departments, menu, sections, tables, printers, then your first
              order.
            </p>

            <div className="mt-6 flex flex-wrap gap-3">
              {nextRecommendedStep ? (
                <Button
                  type="button"
                  onClick={() => {
                    handleStartGuide(nextRecommendedStep);
                  }}
                >
                  Start guided setup
                </Button>
              ) : null}

              {nextRecommendedStep ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(nextRecommendedStep.route)}
                >
                  Open {nextRecommendedStep.title}
                </Button>
              ) : null}

              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/sales')}
              >
                I&apos;ll finish this later
              </Button>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
            <div className="rounded-2xl border border-white/70 bg-white/80 p-5 shadow-sm">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-gray-400">
                    Core progress
                  </p>
                  <p className="mt-2 text-3xl font-semibold text-gray-950">
                    {completedPrimaryCount}/
                    {PRIMARY_FIRST_TIME_ONBOARDING_STEP_IDS.length}
                  </p>
                </div>
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
                  <ListChecks size={22} />
                </div>
              </div>
              <div className="mt-4 h-2 rounded-full bg-emerald-100">
                <div
                  className="h-2 rounded-full bg-pos-primary transition-all duration-300"
                  style={{
                    width: `${Math.round(
                      (completedPrimaryCount /
                        PRIMARY_FIRST_TIME_ONBOARDING_STEP_IDS.length) *
                        100,
                    )}%`,
                  }}
                />
              </div>
            </div>

            <div className="rounded-2xl border border-white/70 bg-[#0f172a] p-5 text-white shadow-sm">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-emerald-200/90">
                    Recommended next
                  </p>
                  <p className="mt-2 text-lg font-semibold">
                    {nextRecommendedStep?.title || 'You are ready to go live'}
                  </p>
                </div>
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 text-emerald-200">
                  <Rocket size={22} />
                </div>
              </div>
              <p className="mt-3 text-sm leading-6 text-slate-300">
                {nextRecommendedStep?.description ||
                  'Your setup checklist is complete. Head to Sales and create your first live order.'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {isBootstrapping ? (
        <div className="rounded-2xl border border-gray-200 bg-white px-6 py-10 text-center text-sm text-gray-500 shadow-sm">
          Loading your onboarding checklist...
        </div>
      ) : (
        <>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-950">
                  Core restaurant setup
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Complete these steps first so orders, KOTs, bills, and
                  settlements work correctly from day one.
                </p>
              </div>
            </div>

            <div className="grid gap-4 xl:grid-cols-2">
              {primarySteps.map((step, index) => (
                <FirstTimeSetupStepCard
                  key={step.id}
                  actionLabel={step.actionLabel}
                  description={step.description}
                  guideLabel="Guide me"
                  icon={step.icon}
                  isCompleted={Boolean(
                    progress?.completed_steps.includes(step.id),
                  )}
                  isSaving={savingStepId === step.id}
                  onStartGuide={() => {
                    handleStartGuide(step);
                  }}
                  onToggleComplete={() => {
                    handleToggleStep(step.id);
                  }}
                  route={step.route}
                  stepNumber={index + 1}
                  title={step.title}
                />
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-950">
                Team and security extras
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                These are not required to go live, but they help you prepare
                your staff and permissions before you scale.
              </p>
            </div>

            <div className="grid gap-4 xl:grid-cols-2">
              {secondarySteps.map((step, index) => (
                <FirstTimeSetupStepCard
                  key={step.id}
                  actionLabel={step.actionLabel}
                  description={step.description}
                  guideLabel="Guide me"
                  icon={step.icon}
                  isCompleted={Boolean(
                    progress?.completed_steps.includes(step.id),
                  )}
                  isOptional
                  isSaving={savingStepId === step.id}
                  onStartGuide={() => {
                    handleStartGuide(step);
                  }}
                  onToggleComplete={() => {
                    handleToggleStep(step.id);
                  }}
                  route={step.route}
                  stepNumber={primarySteps.length + index + 1}
                  title={step.title}
                />
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white px-6 py-5 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-950">
                  Ready to go live?
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Once the core setup is complete, we&apos;ll consider your
                  first-time onboarding finished and send you straight to Sales.
                </p>
              </div>

              <Button
                type="button"
                onClick={handleFinish}
                disabled={!arePrimaryStepsComplete}
                isLoading={isFinishing}
              >
                Open Sales Workspace
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FirstTimeSetupPage;
